import React, { type SyntheticEvent, useRef, useState } from 'react';

import { Button, Tooltip } from '@mui/material';
import { FileUp } from 'lucide-react';

import { useGetProjectById } from '../hooks';
import { useGetActualDataSchemaFiles } from '../hooks/tanstack/useActualDataHook';
import {
  useGetRoles,
  useGetRouteParams,
  useHandleImportActualData,
} from '../hooks/tanstack/useUtilsHook';

import { ActualDataTable } from './actual-data/ActualDataTable';
import ActualDataViewFilesTable from './actual-data/ActualDataViewFilesTable';
import DownloadActualDataTemplatesDialog from './actual-data/DownloadActualDataDialog';
import AlertDialog from './dataset/AlertDialog';
import {
  CustomTabPanel,
  Tab,
  Tabs,
} from './projects/models/ProjectModelsManagement';
import LoadingDialog from './LoadingDialog';
import SuccessDialog from './SuccessDialog';

const TABS = ['Import', 'Data'];

const ProjectActualDataView = () => {
  const [index, setIndex] = useState(0);

  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const { projectRole } = useGetRoles();

  const { orgId, projectId } = useGetRouteParams();

  const { importActualData, error, isPending } = useHandleImportActualData({
    inputFileRef,
    handleError: () => {
      setOpenErrorDialog(true);
    },
  });

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setIndex(newValue);
  };

  const { data: actualDataTemplates } = useGetActualDataSchemaFiles({
    orgId,
    projectId,
  });

  const isDisabled = !actualDataTemplates?.some(
    (file: any) => file.is_imported
  );

  const isAdmin = projectRole === 'project_owner' && !project?.is_guest_project;

  return (
    <div className="flex flex-col gap-4">
      <Tabs value={index} onChange={handleChange}>
        {TABS.map((report) => (
          <Tab
            id={report}
            key={report}
            label={report}
            disabled={report === 'Data' && !!isDisabled}
          />
        ))}
      </Tabs>
      <CustomTabPanel value={index.toString()} index="0">
        <div className="flex flex-col gap-3">
          <h1 className="text-2xl font-bold">Import</h1>
          <div className="flex justify-between">
            <div>
              {actualDataTemplates && (
                <DownloadActualDataTemplatesDialog
                  files={actualDataTemplates}
                  trigger={{
                    style: {
                      color: '#666',
                      borderColor: '#CCC',
                    },
                  }}
                />
              )}
            </div>

            <Tooltip
              title={isAdmin ? '' : 'Only project owners can upload files'}
            >
              <div className="flex gap-4">
                <Button
                  variant="outlined"
                  disabled={!isAdmin}
                  style={{
                    display: 'flex',
                    position: 'relative',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <input
                    ref={inputFileRef}
                    type="file"
                    accept=".xlsx"
                    id="import_ifs"
                    onChange={importActualData}
                    multiple
                    className="absolute w-[140px] cursor-pointer opacity-0"
                  />
                  <FileUp size={20} />
                  Upload File(s)
                </Button>
              </div>
            </Tooltip>
          </div>
          <ActualDataTable />
          <AlertDialog
            title="Error Found: Please Review"
            open={openErrorDialog}
            content={error?.content}
            handleOnClose={() => {
              setOpenErrorDialog(false);
            }}
          />
          <SuccessDialog
            content="Input File Set imported successfully."
            open={openSuccessDialog}
            handleOnClose={() => {
              setOpenSuccessDialog(false);
            }}
          />
          <LoadingDialog title="Uploading" open={isPending} />
        </div>
      </CustomTabPanel>
      <CustomTabPanel value={index.toString()} index="1">
        <div className="font-bold space-y-3">
          <h1 className="text-2xl font-bold">Data</h1>
          <ActualDataViewFilesTable />
        </div>
      </CustomTabPanel>
    </div>
  );
};

export default ProjectActualDataView;
